import { Box, Card, CardContent, FormControl, Grid, MenuItem, Select, Stack, Typography } from '@mui/material';
import * as d3 from 'd3';
import moment from 'moment-timezone';
import { useEffect, useRef, useState } from 'react';
import useSextforceTransactionsHourlySalesData, {
    SextforceTransactionsHourlySalesData,
} from '../../../../hooks/useSextforceTransactionsHourlySalesData';

type MetricType = 'totalAmount' | 'totalTransactions' | 'uniqueFans';

interface SalesHeatmapProps {
    subscriberId?: string | null;
    dateFrom?: Date | null;
    dateTo?: Date | null;
    metric?: MetricType;
    title?: string;
}

// Utility functions
const convertDataToUserTimezone = (
    salesData: SextforceTransactionsHourlySalesData[],
    userTimezone: string,
): SextforceTransactionsHourlySalesData[] => {
    return salesData.map(data => {
        // Assume the server data is in UTC and convert to user's timezone
        // Create a moment object from the date and hour (assuming UTC)
        const utcDateTime = moment.utc(`${data.date} ${data.hour.toString().padStart(2, '0')}:00`, 'YYYY-MM-DD HH:mm');

        // Convert to user's timezone
        const localDateTime = utcDateTime.tz(userTimezone);

        return {
            ...data,
            date: localDateTime.format('YYYY-MM-DD'),
            hour: localDateTime.hour(),
            dayOfWeek: localDateTime.day() + 1, // moment.js uses 0-6, but our data uses 1-7
        };
    });
};

const fillMissingHours = (salesData: SextforceTransactionsHourlySalesData[], date: string): SextforceTransactionsHourlySalesData[] => {
    const existingHours = new Set(salesData.filter(d => d.date === date).map(d => d.hour));
    const filledData = [...salesData];

    for (let hour = 0; hour < 24; hour++) {
        if (!existingHours.has(hour)) {
            filledData.push({
                date,
                hour,
                dayOfWeek: new Date(date).getDay() + 1,
                totalAmount: 0,
                totalTransactions: 0,
                uniqueFans: 0,
                avgAmountPerFan: 0,
                avgAmountPerTransaction: 0,
                fans: [],
            });
        }
    }

    return filledData;
};

const convertTo12HourFormat = (hour: number): string => {
    if (hour === 0) return '12am';
    if (hour < 12) return `${hour}am`;
    if (hour === 12) return '12pm';
    return `${hour - 12}pm`;
};

const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
    }).format(amount);
};

const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        weekday: 'short',
    });
};

const SextforceTransactionsHourlySalesHeatMap = ({ subscriberId = null, dateFrom = null, dateTo = null }: SalesHeatmapProps) => {
    const svgRef = useRef<SVGSVGElement>(null);
    const tooltipRef = useRef<HTMLDivElement>(null);
    const timezone = moment.tz.guess();
    const [metric, setMetric] = useState<MetricType>('totalAmount');
    const [containerWidth, setContainerWidth] = useState<number | null>(null);
    const [containerHeight, setContainerHeight] = useState<number | null>(null);

    const { data: rawSalesData = [], isLoading, error } = useSextforceTransactionsHourlySalesData(subscriberId, dateFrom, dateTo);

    // Convert data to user's timezone
    const salesData = convertDataToUserTimezone(rawSalesData, timezone);

    // Handle container dimensions
    useEffect(() => {
        const updateDimensions = () => {
            if (svgRef.current) {
                const container = svgRef.current.parentElement;
                if (container) {
                    setContainerWidth(container.clientWidth);
                    setContainerHeight(container.clientHeight);
                }
            }
        };

        const resizeObserver = new ResizeObserver(updateDimensions);
        if (svgRef.current?.parentElement) {
            resizeObserver.observe(svgRef.current.parentElement);
        }

        updateDimensions();

        return () => {
            resizeObserver.disconnect();
        };
    }, []);

    // D3 rendering
    useEffect(() => {
        if (!containerWidth || !containerHeight || !salesData.length) {
            return;
        }

        const width = containerWidth;
        const height = Math.max(400, containerHeight); // Reserve space for summary, ensure minimum height
        const margin = { top: 20, right: 10, bottom: 100, left: 70 }; // Increased bottom margin for legend padding

        // Ensure we have enough width for 24 hours
        const minCellWidth = 20; // Minimum width per hour cell
        const minRequiredWidth = margin.left + margin.right + 24 * minCellWidth;

        if (width < minRequiredWidth) {
            // If container is too narrow, we'll need horizontal scrolling or wider container
            console.warn(`Container width ${width}px is too narrow. Minimum required: ${minRequiredWidth}px`);
        }

        // Clear previous renders
        const svg = d3.select(svgRef.current);
        svg.selectAll('*').remove();

        // Prepare data - fill missing hours for each day
        const uniqueDates = [...new Set(salesData.map(d => d.date))].sort();
        let processedData: SextforceTransactionsHourlySalesData[] = [];

        uniqueDates.forEach(date => {
            processedData = fillMissingHours(salesData, date);
        });

        // Create scales
        const yScale = d3
            .scaleBand<string>()
            .domain(uniqueDates)
            .range([margin.top, height - margin.bottom])
            .padding(0.02); // Reduced padding for more space

        const xScale = d3
            .scaleBand<number>()
            .domain(d3.range(0, 24))
            .range([margin.left, width - margin.right])
            .padding(0.02); // Reduced padding for more space

        // Color scale based on metric
        const maxValue = d3.max(salesData, d => d[metric as keyof SextforceTransactionsHourlySalesData] as number) || 0;
        const colorScale = d3.scaleSequential(d3.interpolateBlues).domain([0, maxValue]);

        // Create axes
        const yAxis = d3.axisLeft(yScale).tickFormat(d => formatDate(d));
        const xAxis = d3.axisBottom(xScale).tickFormat(d => {
            // Show every 2nd hour to reduce crowding
            return d % 2 === 0 ? convertTo12HourFormat(d) : '';
        });

        svg.append('g').attr('transform', `translate(${margin.left}, 0)`).call(yAxis).selectAll('text').style('font-size', '12px');

        svg.append('g')
            .attr('transform', `translate(0, ${height - margin.bottom})`)
            .call(xAxis)
            .selectAll('text')
            .style('font-size', '10px');

        // Create heatmap rectangles
        const tooltip = d3.select(tooltipRef.current);

        svg.selectAll('rect')
            .data(processedData)
            .join('rect')
            .attr('x', d => xScale(d.hour) || 0)
            .attr('y', d => yScale(d.date) || 0)
            .attr('width', xScale.bandwidth())
            .attr('height', yScale.bandwidth())
            .attr('fill', d => {
                const value = d[metric as keyof SextforceTransactionsHourlySalesData] as number;
                return value === 0 ? '#f5f5f5' : colorScale(value);
            })
            .attr('stroke', '#fff')
            .attr('stroke-width', 1)
            .style('cursor', 'pointer')
            .on('mouseover', (event: MouseEvent, d) => {
                const tooltipWidth = 250;

                // Get mouse position relative to the SVG element
                let xPos = event.pageX + 10;
                let yPos = event.pageY + 10;

                // Adjust if tooltip would go off screen
                if (xPos + tooltipWidth > window.innerWidth) {
                    xPos = event.clientX - tooltipWidth + 10;
                }

                if (yPos < 0) {
                    yPos = event.clientY + 20;
                }

                tooltip.style('opacity', 1).style('left', `${xPos}px`).style('top', `${yPos}px`).html(`
            <div style="font-weight: bold; margin-bottom: 8px;">
              ${formatDate(d.date)} at ${convertTo12HourFormat(d.hour)}
            </div>
            <div><strong>Revenue:</strong> ${formatCurrency(d.totalAmount || 0)}</div>
            <div><strong>Transactions:</strong> ${d.totalTransactions?.toLocaleString()}</div>
            <div><strong>Unique Fans:</strong> ${d.uniqueFans?.toLocaleString()}</div>
          `);
            })
            .on('mouseout', () => {
                tooltip.style('opacity', 0);
            });

        // Add legend below X axis
        const legendWidth = 200;
        const legendHeight = 10;
        const legendX = (width - legendWidth) / 2; // Center the legend
        const legendY = height - margin.bottom + 50; // More space from X axis

        const legendScale = d3.scaleLinear().domain([0, maxValue]).range([0, legendWidth]);

        const legendAxis = d3
            .axisBottom(legendScale)
            .ticks(5)
            .tickFormat(d => (metric === 'totalAmount' ? formatCurrency(d as number) : (d as number).toString()));

        // Legend gradient
        const defs = svg.append('defs');
        const gradient = defs
            .append('linearGradient')
            .attr('id', 'legend-gradient')
            .attr('x1', '0%')
            .attr('y1', '0%')
            .attr('x2', '100%')
            .attr('y2', '0%');

        gradient
            .selectAll('stop')
            .data(d3.range(0, 1.1, 0.1))
            .join('stop')
            .attr('offset', d => `${d * 100}%`)
            .attr('stop-color', d => colorScale(d * maxValue));

        svg.append('rect')
            .attr('x', legendX)
            .attr('y', legendY)
            .attr('width', legendWidth)
            .attr('height', legendHeight)
            .style('fill', 'url(#legend-gradient)')
            .attr('stroke', '#ccc')
            .attr('stroke-width', 1);

        svg.append('g')
            .attr('transform', `translate(${legendX}, ${legendY + legendHeight})`)
            .call(legendAxis)
            .selectAll('text')
            .style('font-size', '10px');

        svg.append('text')
            .attr('x', legendX + legendWidth / 2)
            .attr('y', legendY - 5)
            .attr('text-anchor', 'middle')
            .style('font-size', '12px')
            .style('font-weight', '500')
            .text(getMetricName(metric));
    }, [containerWidth, containerHeight, salesData, metric]);

    const getMetricName = (metricKey: MetricType): string => {
        const names: Record<MetricType, string> = {
            totalAmount: 'Revenue',
            totalTransactions: 'Transactions',
            uniqueFans: 'Unique Fans',
        };
        return names[metricKey] || metricKey;
    };

    // Calculate peak spending hours
    const getPeakSpendingHours = () => {
        if (!salesData.length) return null;

        // Group by hour and sum the metric values
        const hourlyTotals = new Array(24).fill(0);
        salesData.forEach(d => {
            const value = d[metric as keyof SextforceTransactionsHourlySalesData] as number;
            hourlyTotals[d.hour] += value;
        });

        // Find the top hours (above 80% of max value)
        const maxValue = Math.max(...hourlyTotals);
        const threshold = maxValue * 0.8;
        const peakHours: { hour: number; value: number }[] = [];

        hourlyTotals.forEach((value, hour) => {
            if (value >= threshold && value > 0) {
                peakHours.push({ hour, value });
            }
        });

        // Group consecutive hours into ranges with their total values
        if (peakHours.length === 0) return null;

        const ranges: string[] = [];
        let rangeStart = peakHours[0];
        let rangeEnd = peakHours[0];
        let rangeTotal = peakHours[0].value;

        for (let i = 1; i < peakHours.length; i++) {
            if (peakHours[i].hour === peakHours[i - 1].hour + 1) {
                rangeEnd = peakHours[i];
                rangeTotal += peakHours[i].value;
            } else {
                // End current range and start new one
                const formattedValue = metric === 'totalAmount' ? formatCurrency(rangeTotal) : rangeTotal.toLocaleString();

                if (rangeStart.hour === rangeEnd.hour) {
                    ranges.push(`${convertTo12HourFormat(rangeStart.hour)} (${formattedValue})`);
                } else {
                    ranges.push(`${convertTo12HourFormat(rangeStart.hour)} - ${convertTo12HourFormat(rangeEnd.hour)} (${formattedValue})`);
                }
                rangeStart = peakHours[i];
                rangeEnd = peakHours[i];
                rangeTotal = peakHours[i].value;
            }
        }

        // Add the last range
        const formattedValue = metric === 'totalAmount' ? formatCurrency(rangeTotal) : rangeTotal.toLocaleString();

        if (rangeStart.hour === rangeEnd.hour) {
            ranges.push(`${convertTo12HourFormat(rangeStart.hour)} (${formattedValue})`);
        } else {
            ranges.push(`${convertTo12HourFormat(rangeStart.hour)} - ${convertTo12HourFormat(rangeEnd.hour)} (${formattedValue})`);
        }

        return ranges.join(', ');
    };

    const peakHours = getPeakSpendingHours();

    if (isLoading) {
        return (
            <div
                style={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    height: '400px',
                    fontSize: '16px',
                    color: '#666',
                }}
            >
                Loading sales data...
            </div>
        );
    }

    if (error) {
        return (
            <div
                style={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    height: '400px',
                    fontSize: '16px',
                    color: '#d32f2f',
                }}
            >
                Error loading data. Please try again.
            </div>
        );
    }

    return (
        <Card variant="elevation" sx={{ marginBottom: 4 }}>
            <CardContent>
                <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between" sx={{ marginBottom: 2 }}>
                    <Typography variant="h6">Fans Hourly Spending</Typography>
                    <FormControl>
                        <Select value={metric} onChange={e => setMetric(e.target.value as MetricType)}>
                            <MenuItem value="totalAmount">Revenue</MenuItem>
                            <MenuItem value="totalTransactions">Transactions</MenuItem>
                            <MenuItem value="uniqueFans">Unique Fans</MenuItem>
                        </Select>
                    </FormControl>
                </Stack>
                <Typography variant="body1" sx={{ marginBottom: 2 }}>
                    This heatmap visualizes fan spending patterns across different hours of the day in your local timezone ({timezone}).
                    Hover over each cell to see detailed metrics.
                </Typography>
                <div
                    style={{
                        height: '400px',
                        // minWidth: '800px', // Ensure minimum width
                    }}
                >
                    <div style={{ width: '100%', height: '100%' }}>
                        <svg ref={svgRef} width="100%" height="100%" />
                        <div
                            ref={tooltipRef}
                            style={{
                                position: 'absolute',
                                opacity: 0,
                                background: 'white',
                                border: '1px solid #ccc',
                                borderRadius: '4px',
                                padding: '12px',
                                fontSize: '12px',
                                boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
                                pointerEvents: 'none',
                                maxWidth: '250px',
                                zIndex: 1000,
                            }}
                        />
                    </div>
                </div>
                {/* Summary Section */}
                <Box
                    sx={{
                        backgroundColor: '#f8f9fa',
                        borderTop: '1px solid #e0e0e0',
                        p: 2,
                    }}
                >
                    <Typography variant="subtitle1" component="div" gutterBottom>
                        Peak Spending Analysis (Local Time)
                    </Typography>

                    <Grid container spacing={2} alignItems="flex-start" sx={{ marginBottom: 1 }}>
                        <Grid item xs={12} sm={6} md={4}>
                            <Stack direction="column" spacing={0.5} sx={{ flex: 1 }}>
                                <Typography variant="subtitle2">Peak Hours for {getMetricName(metric)}</Typography>
                                <Typography variant="body1" color="primary.main">
                                    {peakHours || 'No significant peaks detected'}
                                </Typography>
                            </Stack>
                        </Grid>

                        <Grid item xs={12} sm={6} md={4}>
                            <Stack direction="column" spacing={0.5} sx={{ flex: 1 }}>
                                <Typography variant="subtitle2">Total Days Analyzed</Typography>
                                <Typography variant="body1" color="primary.main">
                                    {[...new Set(salesData.map(d => d.date))].length}
                                </Typography>
                            </Stack>
                        </Grid>

                        <Grid item xs={12} sm={6} md={4}>
                            <Stack direction="column" spacing={0.5} sx={{ flex: 1 }}>
                                <Typography variant="subtitle2">Active Hours</Typography>
                                <Typography variant="body1" color="primary.main">
                                    {
                                        salesData.filter(d => {
                                            const val = d[metric as keyof SextforceTransactionsHourlySalesData] as number;
                                            return val > 0;
                                        }).length
                                    }
                                </Typography>
                            </Stack>
                        </Grid>
                    </Grid>
                </Box>
            </CardContent>
        </Card>
    );
};

export default SextforceTransactionsHourlySalesHeatMap;
